import { liveQuery } from "dexie";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { usePosCatalogMediaRuntime } from "../runtime/catalog-media/use-pos-catalog-media-runtime";
import {
  usePosCatalogPriceRuntime,
  type SelectedModifier,
} from "../runtime/catalog-price/use-pos-catalog-price-runtime";
import { storedVariantDisplayLabel, variantDisplayLabel } from "../runtime/catalog-price/variant-presentation";
import { calculateFinalPayableRounding } from "../runtime/commerce/final-payable-rounding";
import { usePosCommerceCalculationRuntime } from "../runtime/commerce/use-pos-commerce-calculation-runtime";
import type {
  CatalogModifierGroupProjection,
  CatalogVariantProjection,
} from "../runtime/gateway/catalog-price-client";
import type { CashShiftSnapshot, EmployeeRuntimeActor } from "../runtime/gateway/identity-client";
import type { PosSalesLayoutDocument } from "../runtime/gateway/sales-layout-client";
import { salesHeaderMessage } from "../runtime/sales-layout/header-message";
import type { LocalShiftState } from "../runtime/local-store/shift-state";
import type { TerminalCredentialState } from "../runtime/local-store/terminal-state";
import { localCartSaleFingerprint } from "../runtime/local-store/cart-state";
import {
  readLocalLoyaltyRedemption,
  type LocalLoyaltyRedemption,
} from "../runtime/local-store/loyalty-redemption-state";
import { readLocalSalesLayoutDocument } from "../runtime/local-store/sales-layout-runtime";
import {
  resolveSalesLayoutMenuOrder,
  resolveSalesLayoutNavigation,
  salesLayoutNavigationMatchesCategory,
} from "../runtime/sales-layout/sales-layout-navigation";
import { SaleProductGrid } from "./SaleProductGrid";
import { VariantSelectionRuntime } from "./VariantSelectionRuntime";

type ShiftDraft = { openingCash: string; pin: string };
type CartSwipeStart = { lineId: string; x: number; y: number };
type ModifierQuantities = Record<string, number>;
type ItemConfigurationMode = "SHARED" | "PER_ITEM";

type Props = {
  actor: EmployeeRuntimeActor;
  terminal: TerminalCredentialState;
  activeShift: CashShiftSnapshot | null;
  shiftState: LocalShiftState | null;
  shiftDraft: ShiftDraft;
  showShiftOpen: boolean;
  busy: boolean;
  online: boolean;
  canOpenShift: boolean;
  onShiftDraftChange: (next: ShiftDraft) => void;
  onShowShiftOpen: () => void;
  onCancelShiftOpen: () => void;
  onOpenShift: (event: React.FormEvent) => void;
  onRefreshShift: () => void;
  onSwitchEmployee: () => void;
  onLogoutEmployee: () => void;
};

function formatMoney(value: number, currency = "THB") {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

function formatDateTime(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" });
}

function hasModifiers(variant: CatalogVariantProjection) {
  return Array.isArray(variant.modifiers) && variant.modifiers.length > 0;
}

function defaultModifierQuantities(variant: CatalogVariantProjection): ModifierQuantities {
  const next: ModifierQuantities = {};
  for (const group of variant.modifiers ?? []) {
    if (group.max_select <= 0) continue;
    const defaults = (group.options ?? []).filter((option) => option.is_default).slice(0, group.max_select);
    for (const option of defaults) next[option.modifier_option_id] = 1;
  }
  return next;
}

function applyModifierDelta(
  current: ModifierQuantities,
  group: CatalogModifierGroupProjection,
  optionId: string,
  delta: number,
) {
  const next = { ...current };
  const currentQuantity = current[optionId] ?? 0;
  if (delta < 0) {
    const value = Math.max(0, currentQuantity - 1);
    if (value === 0) delete next[optionId]; else next[optionId] = value;
    return next;
  }

  if (currentQuantity === 0 && group.max_select === 1) {
    for (const option of group.options ?? []) delete next[option.modifier_option_id];
  } else if (currentQuantity === 0) {
    const selectedCount = (group.options ?? []).filter(
      (option) => (current[option.modifier_option_id] ?? 0) > 0,
    ).length;
    if (selectedCount >= group.max_select) return current;
  }

  const maxQuantity = group.allow_option_quantity ? Math.max(1, group.max_option_quantity) : 1;
  next[optionId] = Math.min(maxQuantity, currentQuantity + 1);
  return next;
}

function modifierRequirementLabel(group: CatalogModifierGroupProjection) {
  const min = Math.max(0, group.min_select);
  const max = Math.max(0, group.max_select);
  if (max === 0) return "ไม่ต้องเลือก";
  if (min === max) return `ต้องเลือก ${min} รายการ`;
  if (min === 0) return `ไม่บังคับ · เลือกได้สูงสุด ${max} รายการ`;
  return `ต้องเลือกอย่างน้อย ${min} · สูงสุด ${max} รายการ`;
}

function modifierSelectionStatus(group: CatalogModifierGroupProjection, selectedCount: number) {
  const requirement = modifierRequirementLabel(group);
  if (selectedCount <= 0 || group.max_select <= 0) return requirement;
  return `${requirement} · เลือกแล้ว ${selectedCount} / ${group.max_select}`;
}

export function PosWorkspace({
  actor,
  terminal,
  activeShift,
  shiftState,
  shiftDraft,
  showShiftOpen,
  busy,
  online,
  canOpenShift,
  onShiftDraftChange,
  onShowShiftOpen,
  onCancelShiftOpen,
  onOpenShift,
  onRefreshShift,
  onSwitchEmployee,
  onLogoutEmployee,
}: Props) {
  const identity = terminal.identity;
  const [query, setQuery] = useState("");
  const [navigationKey, setNavigationKey] = useState("ALL");
  const [salesLayoutDocument, setSalesLayoutDocument] = useState<PosSalesLayoutDocument | null>(null);
  const [selectedMenuId, setSelectedMenuId] = useState<string | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [modifierQuantities, setModifierQuantities] = useState<ModifierQuantities>({});
  const [configuredQuantity, setConfiguredQuantity] = useState(1);
  const [configurationMode, setConfigurationMode] = useState<ItemConfigurationMode>("SHARED");
  const [perItemModifierQuantities, setPerItemModifierQuantities] = useState<ModifierQuantities[]>([]);
  const [editingCartLineId, setEditingCartLineId] = useState<string | null>(null);
  const [revealedCartLineId, setRevealedCartLineId] = useState<string | null>(null);
  const [loyaltyRedemption, setLoyaltyRedemption] = useState<LocalLoyaltyRedemption | null>(null);
  const cartSwipeStartRef = useRef<CartSwipeStart | null>(null);
  const cartSwipeConsumedRef = useRef<string | null>(null);

  const {
    runtime,
    sellableMenus,
    cart,
    status: catalogStatus,
    runtimeError,
    sync: syncCatalog,
    addVariant,
    addConfiguredVariants,
    updateConfiguredVariant,
    updateConfiguredVariants,
    changeQuantity,
    modifierPriceForOption,
  } = usePosCatalogPriceRuntime(terminal, online);
  const { mediaUrlFor } = usePosCatalogMediaRuntime(terminal, online, sellableMenus);
  const commerceCalculation = usePosCommerceCalculationRuntime(terminal);
  const runtimeScopeKey = identity
    ? `${identity.organization_id}:${identity.branch_id}:${identity.node_id}`
    : "";

  useEffect(() => {
    if (!identity || !runtimeScopeKey) {
      setLoyaltyRedemption(null);
      return;
    }
    const subscription = liveQuery(() => readLocalLoyaltyRedemption(identity)).subscribe({
      next: setLoyaltyRedemption,
      error: () => setLoyaltyRedemption(null),
    });
    return () => subscription.unsubscribe();
    // Terminal identity can be rehydrated; scope key is the stable subscription boundary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtimeScopeKey]);

  useEffect(() => {
    if (!identity || !runtimeScopeKey) {
      setSalesLayoutDocument(null);
      return;
    }
    const subscription = liveQuery(() => readLocalSalesLayoutDocument(identity)).subscribe({
      next: setSalesLayoutDocument,
      error: () => setSalesLayoutDocument(null),
    });
    return () => subscription.unsubscribe();
    // Layout is already scoped by trusted terminal identity in IndexedDB.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtimeScopeKey]);

  const catalogCategories = useMemo(() => {
    const categoryById = new Map<string, string>();
    for (const entry of sellableMenus) {
      const categoryId = entry.snapshot.menu.menu_category_id?.trim();
      const categoryName = entry.snapshot.menu.category_name?.trim();
      if (!categoryId || !categoryName || categoryById.has(categoryId)) continue;
      categoryById.set(categoryId, categoryName);
    }
    return Array.from(categoryById, ([categoryId, name]) => ({ categoryId, name }));
  }, [sellableMenus]);

  const navigationItems = useMemo(
    () => resolveSalesLayoutNavigation(salesLayoutDocument, catalogCategories),
    [salesLayoutDocument, catalogCategories],
  );
  const activeNavigation = navigationItems.find((item) => item.key === navigationKey) ?? navigationItems[0];

  useEffect(() => {
    if (!navigationItems.some((item) => item.key === navigationKey)) {
      setNavigationKey("ALL");
    }
  }, [navigationItems, navigationKey]);

  const filteredMenus = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("th");
    if (needle) {
      const allNavigation = navigationItems.find((item) => item.kind === "ALL") ?? navigationItems[0];
      const globallyOrderedMenus = allNavigation
        ? resolveSalesLayoutMenuOrder(allNavigation, sellableMenus)
        : sellableMenus;
      return globallyOrderedMenus.filter((entry) => {
        const menu = entry.snapshot.menu;
        const displayName = menu.effective_card_display_name?.trim() || menu.name;
        return displayName.toLocaleLowerCase("th").includes(needle);
      });
    }

    const navigationMenus = sellableMenus.filter((entry) => (
      salesLayoutNavigationMatchesCategory(activeNavigation, entry.snapshot.menu.menu_category_id)
    ));
    return resolveSalesLayoutMenuOrder(activeNavigation, navigationMenus);
  }, [activeNavigation, navigationItems, query, sellableMenus]);

  const selectedMenu = selectedMenuId
    ? sellableMenus.find((entry) => entry.menuItemId === selectedMenuId) ?? null
    : null;
  const selectedVariantEntry = selectedVariantId && selectedMenu
    ? selectedMenu.sellableVariants.find((entry) => entry.variant.variant_id === selectedVariantId) ?? null
    : null;

  function selectedModifiersFor(quantities: ModifierQuantities): SelectedModifier[] {
    if (!selectedVariantEntry) return [];
    const result: SelectedModifier[] = [];
    for (const group of selectedVariantEntry.variant.modifiers ?? []) {
      for (const option of group.options ?? []) {
        const quantity = quantities[option.modifier_option_id] ?? 0;
        if (quantity <= 0) continue;
        const price = modifierPriceForOption(option.modifier_option_id);
        if (price?.ready) result.push({ group, option, quantity, price });
      }
    }
    return result;
  }
  function modifierSelectionValidFor(quantities: ModifierQuantities) {
    if (!selectedVariantEntry) return false;
    for (const group of selectedVariantEntry.variant.modifiers ?? []) {
      const selected = (group.options ?? []).filter(
        (option) => (quantities[option.modifier_option_id] ?? 0) > 0,
      );
      if (selected.length < group.min_select || selected.length > group.max_select) return false;
      for (const option of selected) {
        const quantity = quantities[option.modifier_option_id] ?? 0;
        if (quantity > Math.max(1, group.max_option_quantity)) return false;
        if (!modifierPriceForOption(option.modifier_option_id)?.ready) return false;
      }
    }
    return true;
  }

  const selectedModifiers = selectedModifiersFor(modifierQuantities);
  const modifierSelectionValid = modifierSelectionValidFor(modifierQuantities);
  const resolvedPerItemConfigurations = configurationMode === "PER_ITEM"
    ? perItemModifierQuantities.map((quantities) => ({
        quantities,
        modifiers: selectedModifiersFor(quantities),
        valid: modifierSelectionValidFor(quantities),
      }))
    : [];
  const configuredSelectionValid = configurationMode === "PER_ITEM"
    ? resolvedPerItemConfigurations.length === configuredQuantity
      && resolvedPerItemConfigurations.every((configuration) => configuration.valid)
    : modifierSelectionValid;

  if (!identity) return null;

  const shiftReady = activeShift?.shift_status === "OPEN";
  const shiftClosing = activeShift?.shift_status === "CLOSING";
  const cartLines = cart?.lines ?? [];
  const cartItemCount = cartLines.reduce((sum, line) => sum + line.quantity, 0);
  const subtotal = cartLines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0);
  const cartFingerprint = cart ? localCartSaleFingerprint(cart) : "";
  const quote = cart?.quote && cart.quote.cartFingerprint === cartFingerprint ? cart.quote : null;
  const summaryCurrency = quote?.currencyCode ?? cartLines[0]?.currencyCode ?? "THB";
  const promotionDiscount = quote?.promotionDiscountTotal ?? 0;
  const rewardPricing = cartLines.length
    && cart
    && loyaltyRedemption?.reservationState === "RESERVED"
    && loyaltyRedemption.cartRequestKey === cart.requestKey
    && loyaltyRedemption.customerId === cart.selectedMember?.customerId
    && (!loyaltyRedemption.validUntil || Date.parse(loyaltyRedemption.validUntil) > Date.now())
    && loyaltyRedemption.pricing?.cartFingerprint === cartFingerprint
    && loyaltyRedemption.pricing.currencyCode === summaryCurrency
    ? loyaltyRedemption.pricing
    : null;
  const rewardDiscount = rewardPricing?.appliedDiscountAmount ?? 0;
  const totalDiscount = promotionDiscount + rewardDiscount;
  const preRoundingTotal = quote?.postPromotionTotal ?? subtotal;
  const baseFinalPayable = quote && commerceCalculation
    ? calculateFinalPayableRounding(quote.postPromotionTotal, commerceCalculation)
    : null;
  const roundingAmount = rewardPricing?.roundingAmount ?? baseFinalPayable?.roundingAmount ?? 0;
  const netTotal = cartLines.length
    ? rewardPricing?.totalAmount ?? baseFinalPayable?.totalAmount ?? preRoundingTotal
    : 0;
  const configuredUnitPrice = selectedVariantEntry
    ? selectedVariantEntry.price.final_price + selectedModifiers.reduce(
        (sum, modifier) => sum + modifier.price.price_delta * modifier.quantity,
        0,
      )
    : 0;
  const configuredBatchTotal = selectedVariantEntry
    ? configurationMode === "PER_ITEM"
      ? resolvedPerItemConfigurations.reduce(
          (sum, configuration) => sum + selectedVariantEntry.price.final_price + configuration.modifiers.reduce(
            (modifierSum, modifier) => modifierSum + modifier.price.price_delta * modifier.quantity,
            0,
          ),
          0,
        )
      : configuredUnitPrice * configuredQuantity
    : 0;
  const selectedVariantLabel = selectedVariantEntry
    ? variantDisplayLabel(selectedVariantEntry.variant)
    : null;
  const productSelectionSummary = selectedVariantEntry
    ? configurationMode === "PER_ITEM"
      ? [
          ...(selectedVariantLabel ? [selectedVariantLabel] : []),
          `${configuredQuantity} รายการ`,
          "ตั้งค่าแยกแต่ละรายการ",
        ].join(" · ")
      : [
          ...(selectedVariantLabel ? [selectedVariantLabel] : []),
          ...selectedModifiers.map((modifier) => (
            `${modifier.option.name}${modifier.quantity > 1 ? ` ×${modifier.quantity}` : ""}`
          )),
          ...(configuredQuantity > 1 ? [`×${configuredQuantity}`] : []),
        ].join(" · ")
    : null;
  const productSelectionPrice = selectedVariantEntry
    ? formatMoney(
        configuredQuantity > 1 ? configuredBatchTotal : configuredUnitPrice,
        selectedVariantEntry.price.currency_code,
      )
    : null;
  const addButtonLabel = editingCartLineId
    ? "บันทึก"
    : configuredQuantity > 1
      ? `เพิ่ม ${configuredQuantity} รายการ`
      : "เพิ่มลงบิล";

  function resetConfigurationDraft() {
    setModifierQuantities({});
    setConfiguredQuantity(1);
    setConfigurationMode("SHARED");
    setPerItemModifierQuantities([]);
  }

  function initializeVariantConfiguration(variant: CatalogVariantProjection) {
    setModifierQuantities(defaultModifierQuantities(variant));
    setConfiguredQuantity(1);
    setConfigurationMode("SHARED");
    setPerItemModifierQuantities([]);
  }

  function resetProductDialog() {
    setEditingCartLineId(null);
    setSelectedMenuId(null);
    setSelectedVariantId(null);
    resetConfigurationDraft();
  }

  const chooseMenu = useCallback(async (menuItemId: string) => {
    const menu = sellableMenus.find((entry) => entry.menuItemId === menuItemId);
    if (!menu) return;

    if (menu.sellableVariants.length === 1) {
      const choice = menu.sellableVariants[0];
      if (hasModifiers(choice.variant)) {
        setSelectedMenuId(menuItemId);
        setSelectedVariantId(choice.variant.variant_id);
        setModifierQuantities(defaultModifierQuantities(choice.variant));
        setConfiguredQuantity(1);
        setConfigurationMode("SHARED");
        setPerItemModifierQuantities([]);
      } else {
        await addVariant(menu, choice.variant, choice.price);
        setQuery("");
      }
      return;
    }

    setSelectedMenuId(menuItemId);
    setSelectedVariantId(null);
    setModifierQuantities({});
    setConfiguredQuantity(1);
    setConfigurationMode("SHARED");
    setPerItemModifierQuantities([]);
  }, [addVariant, sellableMenus]);

  function selectVariant(variant: CatalogVariantProjection) {
    setSelectedVariantId(variant.variant_id);
    initializeVariantConfiguration(variant);
  }

  function changeModifier(group: CatalogModifierGroupProjection, optionId: string, delta: number) {
    setModifierQuantities((current) => applyModifierDelta(current, group, optionId, delta));
  }

  function changePerItemModifier(index: number, group: CatalogModifierGroupProjection, optionId: string, delta: number) {
    setPerItemModifierQuantities((current) => current.map((quantities, itemIndex) => (
      itemIndex === index ? applyModifierDelta(quantities, group, optionId, delta) : quantities
    )));
  }

  function setItemQuantity(nextQuantity: number) {
    const next = Math.max(1, Math.floor(nextQuantity));
    setConfiguredQuantity(next);
    if (next === 1) {
      setConfigurationMode("SHARED");
      setPerItemModifierQuantities([]);
      return;
    }
    if (configurationMode !== "PER_ITEM") return;
    setPerItemModifierQuantities((current) => Array.from(
      { length: next },
      (_, index) => ({ ...(current[index] ?? modifierQuantities) }),
    ));
  }

  function openMultiItemConfiguration() {
    setConfiguredQuantity(2);
    setConfigurationMode("SHARED");
    setPerItemModifierQuantities([]);
  }

  function useSharedConfiguration() {
    setConfigurationMode("SHARED");
    setPerItemModifierQuantities([]);
  }

  function usePerItemConfiguration() {
    setConfigurationMode("PER_ITEM");
    setPerItemModifierQuantities(Array.from(
      { length: configuredQuantity },
      () => ({ ...modifierQuantities }),
    ));
  }

  function openCartLineEditor(lineId: string) {
    if (cartSwipeConsumedRef.current === lineId) {
      cartSwipeConsumedRef.current = null;
      return;
    }
    if (revealedCartLineId === lineId) {
      setRevealedCartLineId(null);
      return;
    }

    const line = cartLines.find((candidate) => candidate.lineId === lineId);
    if (!line?.menuVariantId) return;
    const menu = sellableMenus.find((entry) => {
      const sourceMenuItemId = (entry as typeof entry & { sourceMenuItemId?: string }).sourceMenuItemId;
      return (
        (entry.menuItemId === line.menuItemId || sourceMenuItemId === line.menuItemId)
        && entry.sellableVariants.some((candidate) => candidate.variant.variant_id === line.menuVariantId)
      );
    });
    if (!menu) return;
    const variant = menu.sellableVariants.find(
      (candidate) => candidate.variant.variant_id === line.menuVariantId,
    );
    if (!variant) return;

    const quantities: ModifierQuantities = {};
    for (const modifier of line.modifiers) {
      quantities[modifier.modifierOptionId] = modifier.quantity;
    }

    setRevealedCartLineId(null);
    setEditingCartLineId(lineId);
    setSelectedMenuId(menu.menuItemId);
    setSelectedVariantId(variant.variant.variant_id);
    setModifierQuantities(quantities);
    setConfiguredQuantity(line.quantity);
    setConfigurationMode("SHARED");
    setPerItemModifierQuantities([]);
  }

  async function addConfiguredVariant() {
    if (!selectedMenu || !selectedVariantEntry || !configuredSelectionValid) return;
    const configurations = configurationMode === "PER_ITEM"
      ? resolvedPerItemConfigurations.map((configuration) => configuration.modifiers)
      : Array.from({ length: configuredQuantity }, () => selectedModifiers);
    if (editingCartLineId) {
      await updateConfiguredVariants(
        editingCartLineId,
        selectedMenu,
        selectedVariantEntry.variant,
        selectedVariantEntry.price,
        configurations,
      );
    } else {
      await addConfiguredVariants(
        selectedMenu,
        selectedVariantEntry.variant,
        selectedVariantEntry.price,
        configurations,
      );
    }
    setQuery("");
    resetProductDialog();
  }

  function handleCartTouchStart(lineId: string, event: React.TouchEvent) {
    const touch = event.touches.item(0);
    if (!touch) return;
    cartSwipeConsumedRef.current = null;
    cartSwipeStartRef.current = { lineId, x: touch.clientX, y: touch.clientY };
    if (revealedCartLineId && revealedCartLineId !== lineId) setRevealedCartLineId(null);
  }

  function handleCartTouchEnd(lineId: string, event: React.TouchEvent) {
    const start = cartSwipeStartRef.current;
    const touch = event.changedTouches.item(0);
    cartSwipeStartRef.current = null;
    if (!start || !touch || start.lineId !== lineId) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (Math.abs(deltaX) < 36 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    cartSwipeConsumedRef.current = lineId;
    setRevealedCartLineId(deltaX < 0 ? lineId : null);
  }

  async function removeCartLine(lineId: string, quantity: number) {
    setRevealedCartLineId(null);
    await changeQuantity(lineId, -quantity);
  }

  function renderSharedModifierGroup(group: CatalogModifierGroupProjection) {
    const selectedCount = (group.options ?? []).filter(
      (option) => (modifierQuantities[option.modifier_option_id] ?? 0) > 0,
    ).length;
    return (
      <section className="modifier-group" key={group.modifier_group_id}>
        <div className="modifier-group__header">
          <strong>{group.name}</strong>
          <span>{modifierSelectionStatus(group, selectedCount)}</span>
        </div>
        <div className="modifier-options">
          {(group.options ?? []).map((option) => {
            const quantity = modifierQuantities[option.modifier_option_id] ?? 0;
            const modifierPrice = modifierPriceForOption(option.modifier_option_id);
            const unavailable = !modifierPrice?.ready;
            const selected = quantity > 0;
            const canAdd = group.max_select === 1 || selected || selectedCount < group.max_select;
            return (
              <div className={`modifier-option ${selected ? "modifier-option--selected" : ""} ${unavailable ? "modifier-option--disabled" : ""}`} key={option.modifier_option_id}>
                <button type="button" disabled={unavailable || !canAdd} onClick={() => changeModifier(group, option.modifier_option_id, selected && group.max_option_quantity <= 1 ? -1 : 1)}>
                  <strong>{option.name}</strong>
                  <span>{unavailable ? "ยังไม่มีราคา Published" : modifierPrice.price_delta === 0 ? "ไม่เพิ่มราคา" : `+${formatMoney(modifierPrice.price_delta, modifierPrice.currency_code)}`}</span>
                </button>
                {selected && group.allow_option_quantity && group.max_option_quantity > 1 ? (
                  <div className="modifier-option__qty">
                    <button type="button" onClick={() => changeModifier(group, option.modifier_option_id, -1)}>−</button>
                    <strong>{quantity}</strong>
                    <button type="button" disabled={quantity >= group.max_option_quantity} onClick={() => changeModifier(group, option.modifier_option_id, 1)}>+</button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  function renderPerItemModifierGroup(group: CatalogModifierGroupProjection, quantities: ModifierQuantities, itemIndex: number) {
    const selectedCount = (group.options ?? []).filter(
      (option) => (quantities[option.modifier_option_id] ?? 0) > 0,
    ).length;
    return (
      <div className="modifier-item-group" key={`${itemIndex}:${group.modifier_group_id}`}>
        <div className="modifier-item-group__header">
          <strong>{group.name}</strong>
          <span>{modifierSelectionStatus(group, selectedCount)}</span>
        </div>
        <div className="modifier-item-options">
          {(group.options ?? []).map((option) => {
            const quantity = quantities[option.modifier_option_id] ?? 0;
            const modifierPrice = modifierPriceForOption(option.modifier_option_id);
            const unavailable = !modifierPrice?.ready;
            const selected = quantity > 0;
            const canAdd = group.max_select === 1 || selected || selectedCount < group.max_select;
            return (
              <div className="modifier-item-option-shell" key={`${itemIndex}:${option.modifier_option_id}`}>
                <button
                  type="button"
                  className={`modifier-item-option ${selected ? "modifier-item-option--selected" : ""}`}
                  disabled={unavailable || !canAdd}
                  onClick={() => changePerItemModifier(itemIndex, group, option.modifier_option_id, selected && group.max_option_quantity <= 1 ? -1 : 1)}
                >
                  <strong>{option.name}</strong>
                  {quantity > 1 ? <b>×{quantity}</b> : null}
                  <small>{unavailable ? "ยังไม่มีราคา" : modifierPrice.price_delta === 0 ? "" : `+${formatMoney(modifierPrice.price_delta, modifierPrice.currency_code)}`}</small>
                </button>
                {selected && group.allow_option_quantity && group.max_option_quantity > 1 ? (
                  <div className="modifier-item-option__qty">
                    <button type="button" onClick={() => changePerItemModifier(itemIndex, group, option.modifier_option_id, -1)}>−</button>
                    <strong>{quantity}</strong>
                    <button type="button" disabled={quantity >= group.max_option_quantity} onClick={() => changePerItemModifier(itemIndex, group, option.modifier_option_id, 1)}>+</button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <section className="sale-workspace" aria-label="หน้าขาย POS">
      <header className="sale-header">
        <div className="sale-header__identity">
          <div className="sale-header__mark">M</div>
          <div>
            <p className="eyebrow">MORE POS · SALE WORKSPACE</p>
            <h2>{identity.branch_name}</h2>
            <p className="sale-header__meta">{identity.node_code} · {actor.employee_name} ({actor.employee_code})</p>
          </div>
        </div>
        {salesHeaderMessage(salesLayoutDocument) && <details className="sale-header__message">
          <summary title={salesHeaderMessage(salesLayoutDocument) ?? undefined}>{salesHeaderMessage(salesLayoutDocument)}</summary>
          <p>{salesHeaderMessage(salesLayoutDocument)}</p>
        </details>}
        <div className="sale-header__actions">
          <span className={`sale-status-pill ${online ? "sale-status-pill--ready" : "sale-status-pill--warning"}`}>{online ? "Online" : "Offline"}</span>
          <span className={`sale-status-pill ${shiftReady ? "sale-status-pill--ready" : "sale-status-pill--warning"}`}>
            {shiftClosing ? "กำลังปิดกะ" : shiftReady ? "กะเปิดแล้ว" : "ยังไม่เปิดกะ"}
          </span>
          <button className="sale-header__button" type="button" disabled={busy || !online} onClick={onSwitchEmployee}>เปลี่ยนพนักงาน</button>
          <button className="sale-header__button sale-header__button--quiet" type="button" disabled={busy || !online} onClick={onLogoutEmployee}>ออกจากระบบ</button>
        </div>
      </header>

      <div className="sale-layout">
        <main className="sale-catalog-panel">
          <div className="sale-toolbar">
            <div className="sale-search-shell">
              <span aria-hidden="true">⌕</span>
              <input type="search" placeholder="ค้นหาชื่อเมนู" aria-label="ค้นหาชื่อเมนู" value={query} onChange={(event) => setQuery(event.target.value)} />
              {query.length > 0 ? (
                <button
                  type="button"
                  aria-label="ล้างการค้นหา"
                  title="ล้างการค้นหา"
                  onClick={() => setQuery("")}
                  style={{
                    width: 36,
                    height: 36,
                    flex: "0 0 auto",
                    display: "grid",
                    placeItems: "center",
                    padding: 0,
                    border: 0,
                    borderRadius: 999,
                    background: "#f0ede2",
                    color: "#625f58",
                    fontSize: 22,
                    fontWeight: 800,
                    lineHeight: 1,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              ) : null}
            </div>
            <div className="sale-toolbar__context">
              <span>Rev {runtime.catalogRevision}</span>
              <span>{catalogStatus === "local" || !online ? "Local-first" : "Core synced"}</span>
              {runtimeError ? <button type="button" className="sale-sync-action" onClick={() => void syncCatalog()}>Sync ใหม่</button> : null}
            </div>
          </div>

          <div className="sale-category-strip" aria-label="หมวดสินค้า">
            {navigationItems.map((item) => (
              <button
                type="button"
                key={item.key}
                className={`sale-category-chip ${navigationKey === item.key ? "sale-category-chip--active" : ""}`}
                onClick={() => setNavigationKey(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          {runtimeError && !sellableMenus.length ? (
            <div className="sale-catalog-empty">
              <div className="sale-catalog-empty__icon" aria-hidden="true">!</div>
              <p className="eyebrow">CATALOG RUNTIME</p>
              <h3>ยังโหลด Published Catalog ไม่สำเร็จ</h3>
              <p>{runtimeError}</p>
              <button className="sale-runtime-retry" type="button" disabled={!online} onClick={() => void syncCatalog()}>ลอง Sync ใหม่</button>
            </div>
          ) : filteredMenus.length ? (
            <SaleProductGrid
              menus={filteredMenus}
              mediaUrlFor={mediaUrlFor}
              onChooseMenu={chooseMenu}
              showCategory={activeNavigation?.kind === "ALL"}
            />
          ) : (
            <div className="sale-catalog-empty">
              <div className="sale-catalog-empty__icon" aria-hidden="true">☕</div>
              <p className="eyebrow">PUBLISHED LOCAL CATALOG</p>
              <h3>{catalogStatus === "syncing" || catalogStatus === "loading" ? "กำลังเตรียมเมนูจริง" : "ไม่พบเมนูที่ขายได้"}</h3>
              <p>POS จะแสดงเฉพาะ Published Catalog ที่มี Published + Effective + Eligible Price สำหรับสาขาและช่องทางของเครื่องนี้</p>
            </div>
          )}
        </main>

        <aside className="sale-cart-panel" aria-label="ตะกร้าขาย">
          <div className="sale-cart-header">
            <div><p className="eyebrow">CURRENT SALE</p><h3>บิลปัจจุบัน</h3></div>
            <span className="sale-count-pill">{cartItemCount} รายการ</span>
          </div>
          <div className="sale-cart-body">
            {cartLines.length ? (
              <div className="sale-cart-lines">
                {cartLines.map((line) => (
                  <div
                    className={`sale-cart-line-shell ${revealedCartLineId === line.lineId ? "sale-cart-line-shell--revealed" : ""}`}
                    key={line.lineId}
                    onTouchStart={(event) => handleCartTouchStart(line.lineId, event)}
                    onTouchEnd={(event) => handleCartTouchEnd(line.lineId, event)}
                    onTouchCancel={() => { cartSwipeStartRef.current = null; }}
                  >
                    <button
                      className="sale-cart-line__delete"
                      type="button"
                      onClick={() => void removeCartLine(line.lineId, line.quantity)}
                      aria-label={`ลบ ${line.menuName} ออกจากบิล`}
                    >
                      ลบ
                    </button>
                    <article
                      className="sale-cart-line"
                      role="button"
                      tabIndex={0}
                      title="แตะเพื่อแก้ไขตัวเลือก"
                      aria-label={`แก้ไขตัวเลือก ${line.menuName}`}
                      onClick={() => openCartLineEditor(line.lineId)}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key !== "Enter" && event.key !== " ") return;
                        event.preventDefault();
                        openCartLineEditor(line.lineId);
                      }}
                    >
                      <div className="sale-cart-line__copy">
                        <strong>{line.menuName}</strong>
                        {storedVariantDisplayLabel(line.variantLabel) ? <small>{storedVariantDisplayLabel(line.variantLabel)}</small> : null}
                        {line.modifiers.map((modifier) => (
                          <small className="sale-cart-line__modifier" key={`${line.lineId}:${modifier.modifierOptionId}`}>
                            + {modifier.modifierOptionName}{modifier.quantity > 1 ? ` ×${modifier.quantity}` : ""}{modifier.priceDelta ? ` (${formatMoney(modifier.priceDelta * modifier.quantity, line.currencyCode)})` : ""}
                          </small>
                        ))}
                        <span>{formatMoney(line.unitPrice, line.currencyCode)}</span>
                      </div>
                      <div className="sale-cart-line__qty">
                        <button type="button" onClick={(event) => { event.stopPropagation(); void changeQuantity(line.lineId, -1); }} aria-label={`ลด ${line.menuName}`}>−</button>
                        <strong>{line.quantity}</strong>
                        <button type="button" onClick={(event) => { event.stopPropagation(); void changeQuantity(line.lineId, 1); }} aria-label={`เพิ่ม ${line.menuName}`}>+</button>
                      </div>
                      <strong className="sale-cart-line__amount">{formatMoney(line.unitPrice * line.quantity, line.currencyCode)}</strong>
                    </article>
                  </div>
                ))}
              </div>
            ) : <div className="sale-cart-empty"><strong>ยังไม่มีสินค้าในบิล</strong><p>เลือกเมนูเพื่อเพิ่มรายการ</p></div>}
          </div>
          <div className="sale-cart-summary">
            <div><span>ยอดสินค้า</span><strong>{formatMoney(subtotal, summaryCurrency)}</strong></div>
            <div><span>ส่วนลด / โปรโมชั่น</span><strong>{formatMoney(totalDiscount, summaryCurrency)}</strong></div>
            {Math.abs(roundingAmount) >= 0.005 ? (
              <div><span>ปัดเศษยอดชำระ</span><strong>{formatMoney(roundingAmount, summaryCurrency)}</strong></div>
            ) : null}
            <div className="sale-cart-summary__total"><span>ยอดชำระ</span><strong>{formatMoney(netTotal, summaryCurrency)}</strong></div>
          </div>
          {!shiftReady ? (
            <div className="sale-shift-card">
              {activeShift ? (
                <>
                  <div className="sale-shift-card__title">
                    <div>
                      <span className={`sale-shift-dot ${shiftClosing ? "sale-shift-dot--warning" : ""}`} aria-hidden="true" />
                      <strong>{shiftClosing ? "กำลังปิดกะ" : "สถานะกะต้องตรวจสอบ"}</strong>
                    </div>
                    <button type="button" disabled={!online || busy} onClick={onRefreshShift}>รีเฟรช</button>
                  </div>
                  <p>ผู้รับผิดชอบ {activeShift.responsible_employee_name} · เปิด {formatDateTime(activeShift.opened_at)}</p>
                  <small>{shiftState?.source === "LOCAL" ? "Local recovery" : "Core verified"}</small>
                </>
              ) : (
                <>
                  <div className="sale-shift-card__title"><div><span className="sale-shift-dot sale-shift-dot--warning" aria-hidden="true" /><strong>ยังไม่เปิดกะ</strong></div></div>
                  <p>เลือกสินค้าและจัดบิลได้ตามปกติ แต่ Final Sale Commit ต้องมี Active POS Shift ก่อน</p>
                  {canOpenShift ? <button className="sale-open-shift" type="button" disabled={busy || !online} onClick={onShowShiftOpen}>เปิดกะ</button> : <div className="locked-note">Policy ขององค์กรไม่อนุญาตให้พนักงานคนนี้เปิดกะ</div>}
                </>
              )}
            </div>
          ) : null}
          <button className="sale-checkout" type="button" disabled>ชำระเงิน</button>
          <p className="sale-checkout-note">Cart/Price เป็นข้อมูลจริงแล้ว · Checkout/Final Sale Commit จะเชื่อม Commerce Contract ใน Slice ถัดไป</p>
        </aside>
      </div>

      {selectedMenu ? (
        <div className="pos-modal-backdrop" role="presentation">
          <section className="pos-modal pos-product-modal" role="dialog" aria-modal="true" aria-labelledby="product-variant-title">
            <div className="pos-modal__header">
              <div>
                <p className="eyebrow">{editingCartLineId ? "แก้ไขรายการ" : "PUBLISHED OPTIONS"}</p>
                <div className="pos-product-title-row">
                  <h2 id="product-variant-title">{selectedMenu.snapshot.menu.name}</h2>
                  {productSelectionPrice ? (
                    <div className="pos-product-selection-summary" aria-label="ตัวเลือกและราคาปัจจุบัน">
                      {productSelectionSummary ? <p className="modifier-variant-label">{productSelectionSummary}</p> : null}
                      <div className="pos-product-selection-price">
                        <span>{configuredQuantity > 1 ? "ราคารวม" : "ราคา"}</span>
                        <strong>{productSelectionPrice}</strong>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "0 0 auto" }}>
                {selectedVariantEntry ? (
                  <button
                    className="primary-action"
                    type="button"
                    disabled={!configuredSelectionValid}
                    onClick={() => void addConfiguredVariant()}
                    style={{ width: "auto", minHeight: 42, marginTop: 0, padding: "0 18px", whiteSpace: "nowrap" }}
                  >
                    {addButtonLabel}
                  </button>
                ) : null}
                <button type="button" className="pos-modal__close" onClick={resetProductDialog} aria-label="ปิดตัวเลือกสินค้า">×</button>
              </div>
            </div>

            <div className="pos-product-flow">
              {selectedMenu.sellableVariants.length > 1 || (selectedMenu.snapshot.selection_dimensions?.length ?? 0) > 0 ? (
                <VariantSelectionRuntime
                  menuName={selectedMenu.snapshot.menu.name}
                  variants={selectedMenu.sellableVariants}
                  selectionDimensions={selectedMenu.snapshot.selection_dimensions ?? []}
                  initialVariantId={editingCartLineId ? selectedVariantId : null}
                  mediaUrl={mediaUrlFor(selectedMenu.snapshot.product_media?.primary_media)}
                  mediaAlt={selectedMenu.snapshot.product_media?.primary_media?.alt_text ?? selectedMenu.snapshot.menu.name}
                  mediaFitMode={selectedMenu.snapshot.product_media?.primary_media?.fit_mode ?? "COVER"}
                  mediaFocalPoint={selectedMenu.snapshot.product_media?.primary_media?.focal_point ?? null}
                  onSelectionChange={() => {
                    if (selectedMenu.sellableVariants.length > 1) {
                      setSelectedVariantId(null);
                      resetConfigurationDraft();
                    }
                  }}
                  onResolve={(variant, price) => {
                    if (hasModifiers(variant)) {
                      selectVariant(variant);
                    } else {
                      void (async () => {
                        if (editingCartLineId) {
                          await updateConfiguredVariant(editingCartLineId, selectedMenu, variant, price, []);
                        } else {
                          await addVariant(selectedMenu, variant, price);
                        }
                        setQuery("");
                        resetProductDialog();
                      })();
                    }
                  }}
                />
              ) : null}

              {selectedVariantEntry ? (
                <div className="modifier-runtime">
                  {configuredQuantity === 1 ? (
                    <button className="modifier-multi-entry" type="button" onClick={openMultiItemConfiguration}>
                      + หลายรายการ
                    </button>
                  ) : (
                    <section className="modifier-batch-toolbar" aria-label="ตั้งค่าหลายรายการ">
                      <div className="modifier-batch-quantity">
                        <span>จำนวน</span>
                        <div>
                          <button type="button" onClick={() => setItemQuantity(configuredQuantity - 1)}>−</button>
                          <strong>{configuredQuantity}</strong>
                          <button type="button" onClick={() => setItemQuantity(configuredQuantity + 1)}>+</button>
                        </div>
                      </div>
                      <div className="modifier-batch-mode" role="group" aria-label="รูปแบบการตั้งค่าหลายรายการ">
                        <button type="button" className={configurationMode === "SHARED" ? "modifier-batch-mode--active" : ""} onClick={useSharedConfiguration}>เหมือนกันทุกรายการ</button>
                        <button type="button" className={configurationMode === "PER_ITEM" ? "modifier-batch-mode--active" : ""} onClick={usePerItemConfiguration}>แยกแต่ละรายการ</button>
                      </div>
                    </section>
                  )}

                  {configurationMode === "SHARED" ? (
                    (selectedVariantEntry.variant.modifiers ?? []).map(renderSharedModifierGroup)
                  ) : (
                    <div className="modifier-per-item-list" aria-label="ตั้งค่า Modifier แยกแต่ละรายการ">
                      {perItemModifierQuantities.map((quantities, itemIndex) => {
                        const resolved = resolvedPerItemConfigurations[itemIndex];
                        const itemTotal = selectedVariantEntry.price.final_price + (resolved?.modifiers ?? []).reduce(
                          (sum, modifier) => sum + modifier.price.price_delta * modifier.quantity,
                          0,
                        );
                        const itemSummary = (resolved?.modifiers ?? []).map((modifier) => (
                          `${modifier.option.name}${modifier.quantity > 1 ? ` ×${modifier.quantity}` : ""}`
                        )).join(" · ");
                        return (
                          <section className={`modifier-item-card ${resolved?.valid ? "" : "modifier-item-card--invalid"}`} key={`item-${itemIndex}`}>
                            <div className="modifier-item-card__header">
                              <div>
                                <strong>รายการ {itemIndex + 1}</strong>
                                <small>{itemSummary || "ยังไม่ได้เลือกตัวเลือก"}</small>
                              </div>
                              <b>{formatMoney(itemTotal, selectedVariantEntry.price.currency_code)}</b>
                            </div>
                            {(selectedVariantEntry.variant.modifiers ?? []).map((group) => (
                              renderPerItemModifierGroup(group, quantities, itemIndex)
                            ))}
                          </section>
                        );
                      })}
                    </div>
                  )}

                  <div className="modifier-total">
                    <span>{configuredQuantity > 1 ? `รวม ${configuredQuantity} รายการ` : "ราคาต่อรายการ"}</span>
                    <strong>{formatMoney(configuredQuantity > 1 ? configuredBatchTotal : configuredUnitPrice, selectedVariantEntry.price.currency_code)}</strong>
                  </div>
                  <button className="primary-action modifier-add-cart" type="button" disabled={!configuredSelectionValid} onClick={() => void addConfiguredVariant()}>{addButtonLabel}</button>
                  <p className="form-note">
                    {editingCartLineId
                      ? configuredQuantity > 1
                        ? configurationMode === "PER_ITEM"
                          ? `กำลังตั้งค่าแยก ${configuredQuantity} รายการ`
                          : `การตั้งค่านี้จะใช้กับทั้ง ${configuredQuantity} รายการ`
                        : "แก้ไขตัวเลือกแล้วกดบันทึก"
                      : "ใช้เฉพาะ Modifier Assignment จาก Published Catalog และราคา Modifier ที่ Published + Effective + Eligible เท่านั้น · หลายรายการใช้กฎเดียวกันและเพิ่มลง Cart จาก Local Runtime ในครั้งเดียว"}
                  </p>
                </div>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      {showShiftOpen ? (
        <div className="pos-modal-backdrop" role="presentation">
          <section className="pos-modal" role="dialog" aria-modal="true" aria-labelledby="open-shift-title">
            <div className="pos-modal__header">
              <div><p className="eyebrow">OPEN POS SHIFT</p><h2 id="open-shift-title">เปิดกะ</h2></div>
              <button type="button" className="pos-modal__close" disabled={busy} onClick={onCancelShiftOpen} aria-label="ปิดหน้าต่างเปิดกะ">×</button>
            </div>
            <form onSubmit={onOpenShift}>
              <p className="form-note">ผู้รับผิดชอบกะ: {actor.employee_name} ({actor.employee_code})</p>
              <label><span>เงินสดตั้งต้นในลิ้นชัก (ใส่ 0 หากไม่มีเงินสด)</span><input required type="number" min="0" step="0.01" inputMode="decimal" placeholder="เช่น 1000 หรือ 0" value={shiftDraft.openingCash} onChange={(event) => onShiftDraftChange({ ...shiftDraft, openingCash: event.target.value })} /></label>
              <label><span>PIN เพื่อยืนยันการเปิดกะ</span><input required type="password" inputMode="numeric" autoComplete="one-time-code" name="pos-shift-confirmation-pin" value={shiftDraft.pin} onChange={(event) => onShiftDraftChange({ ...shiftDraft, pin: event.target.value })} /></label>
              <button className="primary-action" type="submit" disabled={busy || !online || !canOpenShift}>{busy ? "กำลังเปิดกะ…" : "เปิดกะ"}</button>
              <button className="secondary-action" type="button" disabled={busy} onClick={onCancelShiftOpen}>ยกเลิก</button>
              <p className="form-note">Opening Cash ต้องยืนยันทุกกะ และ 0 บาทเป็นค่าที่ถูกต้อง · PIN ไม่ถูก Persist</p>
            </form>
          </section>
        </div>
      ) : null}
    </section>
  );
}
